import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Autocomplete,
  CircularProgress,
  InputAdornment,
  TextField,
  Box,
} from "@mui/material";
import axiosInstant from "../../../api";
import { BrandV2Modal } from "../../brand/BrandV2Modal";
import type { BrandFormHiddenField } from "../../brand/BrandForm";
import { selectorAutocompleteStyle } from "./selectorStyles";

const LOGO_TOKEN = "pk_fw3EgsMdRnKqF-72FUrrmQ";

/** A brand as returned by `GET /brands` (rows). Loosely typed — rows are partial. */
export interface Brand {
  id?: number | string;
  name: string;
  logo?: string;
  cover_image?: string;
  rmn?: boolean;
  [key: string]: any;
}

/**
 * Synthetic option injected into the dropdown so the user can create a brand-new
 * brand inline when their search matches nothing.
 */
interface AddNewBrandOption {
  addNew: true;
  /** Display label, e.g. `Add "Acme"`. */
  name: string;
  /** Raw text the user typed, used to pre-fill the create-brand modal. */
  inputValue: string;
}

type BrandOption = Brand | AddNewBrandOption;

const isAddNewBrandOption = (option: BrandOption): option is AddNewBrandOption =>
  (option as AddNewBrandOption).addNew === true;

/** Stable identity for a brand across the API shapes (id, falling back to name). */
const brandKey = (brand: Brand): string | number => brand.id ?? brand.name;

function getBrandLogoUrl(brand?: Brand | null) {
  const logo = brand?.logo ?? brand?.cover_image;
  if (logo && typeof logo === "string" && logo.trim()) return logo;
  const name = brand?.name?.replace(/\s+/g, "") || "brand";
  return `https://img.logo.dev/${name}.com?token=${LOGO_TOKEN}`;
}

export interface InlineBrandSelectorProps {
  /** Formik `setFieldValue`. Writes the selected brand to `brand`/`brands`. */
  setFieldValue: (field: string, value: any) => void;
  selectedBrand: any;
  setSelectedBrand: (brand: any) => void;
  disabled?: boolean;
  /** When true, the `/brands` search adds `rmn: true`. */
  isRMN?: boolean;
  /** Client-side option filter: true → only RMN, false → only non-RMN, undefined → no filter. */
  filterRmn?: boolean;
  /** When true, an `Add "<text>"` option opens the create-brand modal. */
  allowAdd?: boolean;
  /** Forwarded to the create-brand modal: offer the role-gated parent/RMN toggle. */
  showRmnToggle?: boolean;
  /**
   * When provided, the dropdown is restricted to exactly these brands (filtered
   * client-side as the user types) and the `/brands` search is disabled. Used to
   * scope the picker to a caller-supplied set — e.g. an RMN's associated brands.
   */
  scopedOptions?: Brand[];
}

const InlineBrandSelector: React.FC<InlineBrandSelectorProps> = ({
  setFieldValue,
  selectedBrand,
  setSelectedBrand,
  disabled = false,
  isRMN = false,
  filterRmn,
  allowAdd = false,
  showRmnToggle = false,
  scopedOptions,
}) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchInput = useRef("");
  // Monotonic id used to discard responses from superseded (out-of-order) requests.
  const requestIdRef = useRef(0);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<Brand[]>([]);
  const [inputText, setInputText] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [pendingBrandName, setPendingBrandName] = useState("");

  useEffect(() => {
    if (!selectedBrand) setInputText("");
  }, [selectedBrand]);

  // Clear any pending debounced fetch when the component unmounts.
  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  const searchBrands = async () => {
    if (!searchInput.current.trim()) {
      setOptions([]);
      setHasSearched(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    setHasSearched(true);
    try {
      const params = {
        search: searchInput.current,
        ...(isRMN ? { rmn: true } : {}),
      };
      const { data } = await axiosInstant.get<{ rows?: Brand[] }>("/brands", { params });
      // A newer request started since this one; ignore the stale response.
      if (requestId !== requestIdRef.current) return;
      setOptions(data.rows || []);
    } catch {
      if (requestId === requestIdRef.current) setOptions([]);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  // When `scopedOptions` is supplied the picker is restricted to that set (no
  // `/brands` search); otherwise it uses the fetched search results.
  const isScoped = Array.isArray(scopedOptions);
  const baseOptions: Brand[] = isScoped ? (scopedOptions as Brand[]) : options;
  let filteredOptions: Brand[] = baseOptions;
  if (typeof filterRmn === "boolean") {
    filteredOptions = filterRmn
      ? baseOptions.filter((opt) => !!opt.rmn)
      : baseOptions.filter((opt) => !opt.rmn);
  }

  // Fields hidden in the create-brand modal so it shows only the essentials:
  // name, description, logo, and (when allowed) the role-gated network toggle.
  const modalHiddenFields = useMemo<BrandFormHiddenField[]>(() => {
    const hidden: BrandFormHiddenField[] = [
      "embed",
      "preApproval",
      "notifications",
      "contentTypes",
    ];
    if (!showRmnToggle) hidden.push("plan");
    return hidden;
  }, [showRmnToggle]);

  const handleSelectBrand = (brand: Brand | null) => {
    setSelectedBrand(brand);
    setFieldValue("brands", brand ? [brand] : []);
  };

  const handleBrandCreated = (createdBrand: Brand) => {
    handleSelectBrand(createdBrand);
    setFieldValue("brand", createdBrand?.name);
    setInputText(createdBrand?.name || "");
    searchInput.current = createdBrand?.name || "";
    setShowBrandModal(false);
    setPendingBrandName("");
  };

  const noOptionsText =
    !isScoped && hasOpened && !hasSearched
      ? "Start typing to see brand suggestions"
      : "No options";

  const showLogo = !!selectedBrand && !selectedBrand.name?.startsWith('Add "');

  return (
    <Box>
      <Autocomplete
        open={open}
        onOpen={() => {
          setOpen(true);
          setHasOpened(true);
        }}
        onClose={() => {
          setOpen(false);
          if (!inputText.trim()) {
            setHasSearched(false);
            setOptions([]);
          }
        }}
        isOptionEqualToValue={(option, value) =>
          !isAddNewBrandOption(option) &&
          !isAddNewBrandOption(value) &&
          brandKey(option) === brandKey(value)
        }
        getOptionLabel={(option) => option.name || ""}
        // Widen to BrandOption[] so MUI infers the option type as the
        // Brand | AddNewBrandOption union (filterOptions injects AddNewBrandOption).
        options={filteredOptions as BrandOption[]}
        value={selectedBrand || null}
        inputValue={inputText}
        disabled={disabled}
        disableClearable={!selectedBrand}
        noOptionsText={noOptionsText}
        onInputChange={(_, newVal) => {
          searchInput.current = newVal;
          setInputText(newVal);
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          if (isScoped) {
            // Options are preloaded; MUI's filterOptions handles substring
            // matching, so no remote `/brands` search is needed.
            return;
          }
          if (newVal.trim()) {
            timeoutRef.current = setTimeout(() => {
              searchBrands();
            }, 500);
          } else {
            setOptions([]);
            setHasSearched(false);
          }
        }}
        onChange={(_, value) => {
          if (!value) {
            handleSelectBrand(null);
            setFieldValue("brand", "");
            setInputText("");
            searchInput.current = "";
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            setOptions([]);
            setHasSearched(false);
            return;
          }
          if (isAddNewBrandOption(value)) {
            setPendingBrandName(value.inputValue);
            setShowBrandModal(true);
            setInputText("");
            searchInput.current = "";
            return;
          }
          handleSelectBrand(value);
          setFieldValue("brand", value.name);
        }}
        loading={loading}
        filterOptions={(opts, state) => {
          const filtered = opts.filter(
            (opt) =>
              !isAddNewBrandOption(opt) &&
              // `/brands` rows are loosely typed and can be partial — guard
              // `name` so a row without one doesn't crash the Autocomplete.
              (opt.name || "").toLowerCase().includes(state.inputValue.toLowerCase())
          );
          if (
            allowAdd &&
            state.inputValue !== "" &&
            !opts.some((opt) => !isAddNewBrandOption(opt) && opt.name === state.inputValue)
          ) {
            const addOption: AddNewBrandOption = {
              addNew: true,
              name: `Add "${state.inputValue}"`,
              inputValue: state.inputValue,
            };
            filtered.push(addOption);
          }
          return filtered;
        }}
        renderOption={(props, option) => {
          const isAdd = isAddNewBrandOption(option);
          return (
            <li {...props} key={isAdd ? option.name : brandKey(option)}>
              {!isAdd && (
                <img
                  src={getBrandLogoUrl(option)}
                  alt=""
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 5,
                    objectFit: "cover",
                    marginRight: 10,
                    flexShrink: 0,
                  }}
                />
              )}
              <span style={{ fontFamily: "CircularStd", fontSize: 14 }}>{option.name}</span>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label=""
            placeholder="Select Brand"
            fullWidth
            sx={selectorAutocompleteStyle}
            InputProps={{
              ...params.InputProps,
              startAdornment: showLogo ? (
                <InputAdornment position="start" sx={{ ml: "4px", mr: 0 }}>
                  <img
                    src={getBrandLogoUrl(selectedBrand)}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                    style={{ width: 22, height: 22, borderRadius: 5, objectFit: "cover" }}
                  />
                </InputAdornment>
              ) : null,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {showBrandModal && (
        <BrandV2Modal
          initialBrandName={pendingBrandName}
          hiddenFields={modalHiddenFields}
          onClose={() => {
            setShowBrandModal(false);
            setPendingBrandName("");
          }}
          onComplete={handleBrandCreated}
        />
      )}
    </Box>
  );
};

export default InlineBrandSelector;
